import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, isRef as _isRef, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, toRef, watch } from 'vue';
    import { Tabs } from '@/components/ui/tabs';
    import { useFormErrorTabs } from '@/composables/useFormErrorTabs';
    import EmployeeEmploymentForm from '@/components/employees/EmployeeEmploymentForm.vue';
    import EmployeeOptionalForm from '@/components/employees/EmployeeOptionalForm.vue';
    import EmployeePersonalForm from '@/components/employees/EmployeePersonalForm.vue';
    import EmployeeAddressForm from '@/components/employees/EmployeeAddressForm.vue';
    import EmployeeBankForm from '@/components/employees/EmployeeBankForm.vue';
    import EmployeePensionForm from '@/components/employees/EmployeePensionForm.vue';
    import EmployeeCreditCardsForm from '@/components/employees/EmployeeCreditCardsForm.vue';
    import EmployeeUserForm from '@/components/employees/EmployeeUserForm.vue';
    import { useDanmarksStatistikData } from '@/composables/useDanmarksStatistikData';
    import { useCompanyData } from '@/composables/useCompany';
    import { useProductionUnitsData } from '@/composables/useProductionUnitsData';
    import { useDepartmentsData } from '@/composables/useDepartmentsData';
    import { useEmployeeCompaniesData } from '@/composables/useEmployeeCompaniesData';
    import { useEmployeeCprData } from '@/composables/useEmployeeCprData';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData, Employee } from '@/types';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeTabs',
  props: {
    form: {},
    employee: {}
  },
  setup(__props: any) {

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const isClient = computed(() => page.props.auth.user.is_client);

    const props = __props;

    const { companyData } = useCompanyData(selectedCompanyId.value, !isClient.value);
    const { danmarksStatistikData } = useDanmarksStatistikData(
        toRef(props.form.employment, 'danmarks_statistik'),
    );
    const { productionUnitsData } = useProductionUnitsData(
        companyData.value?.employees_optional?.production_unit,
        selectedCompanyId.value,
    );
    const { departmentsData } = useDepartmentsData(selectedCompanyId.value);
    const { companiesData } = useEmployeeCompaniesData();
    const { cprData } = useEmployeeCprData(props.form.customer_employees.id);

    const hasOptional = computed(() => {
        return (
            companyData.value?.employees_optional?.has_free_car ||
            companyData.value?.employees_optional?.kilometer ||
            companyData.value?.employees_optional?.has_free_phone ||
            companyData.value?.employees_optional?.has_lunch_arrangement ||
            companyData.value?.employees_optional?.has_fixed_add_ons ||
            companyData.value?.employees_optional?.has_pension ||
            companyData.value?.employees_optional?.has_health_insurance ||
            companyData.value?.employees_optional?.danmarks_statistik
        );
    });

    watch(cprData, (newCprData) => {
        props.form.customer_employees.cpr = newCprData;
    });

    const tabs = computed(() => {
        return [
            { value: 'employment', label: 'employee.employment.header' },
            ...(hasOptional.value
                ? [{ value: 'optional', label: 'employee.optional.header' }]
                : []),
            { value: 'personal', label: 'employee.personal.header' },
            { value: 'address', label: 'employee.address.header' },
            { value: 'bank', label: 'employee.bank.header' },
            ...(props.form.employment.has_pension
                ? [{ value: 'pension', label: 'employee.pension.header' }]
                : []),
            { value: 'credit_cards', label: 'employee.credit_cards.header' },
            { value: 'user', label: 'employee.user.header' },
        ];
    });

    const fieldToTabMap: Record<string, string> = {
        'employment.salary': 'employment',
        'employment.tax_card': 'employment',
        'employment.weekly_working_days': 'employment',
        'employment.weekly_working_hours': 'employment',
        'customer_employees.employment_date': 'employment',

        'employment.disco_code': 'optional',
        'employment.dst_hired_terms': 'optional',
        'employment.dst_job_status': 'optional',
        'employment.dst_salary_form': 'optional',
        'employment.dst_time_limited_employment': 'optional',
        'employment.fixed_add_ons_monthly': 'optional',
        'employment.lunch_arrangement_monthly': 'optional',

        'customer_employees.cpr': 'personal',
        'customer_employees.email': 'personal',
        'customer_employees.first_name': 'personal',
        'customer_employees.last_name': 'personal',

        'address.main.address': 'address',
        'address.main.city': 'address',
        'address.main.postal_code': 'address',

        'bank.bank_account': 'bank',
        'bank.bank_reg': 'bank',

        'companyPension.company_contribution': 'pension',
        'companyPension.employee_contribution': 'pension',
        'companyPension.pension_company': 'pension',
        'companyPension.type': 'pension',

        'user.id': 'user',
    };

    const { currentTab } = useFormErrorTabs(props.form, fieldToTabMap, 'employment');

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Tabs), {
    tabs: tabs.value,
    modelValue: _unref(currentTab),
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(currentTab) ? (currentTab).value = $event : null))
  }, {
    employment: _withCtx(() => [
      _createVNode(EmployeeEmploymentForm, {
        form: _ctx.form,
        company: _unref(companyData),
        employee: _ctx.employee
      }, null, 8, ["form", "company", "employee"])
    ]),
    optional: _withCtx(() => [
      _createVNode(EmployeeOptionalForm, {
        form: _ctx.form,
        company: _unref(companyData),
        "danmarks-statistik-data": _unref(danmarksStatistikData),
        employee: _ctx.employee
      }, null, 8, ["form", "company", "danmarks-statistik-data", "employee"])
    ]),
    personal: _withCtx(() => [
      _createVNode(EmployeePersonalForm, {
        form: _ctx.form,
        company: _unref(companyData),
        "production-units-data": _unref(productionUnitsData),
        "departments-data": _unref(departmentsData),
        "companies-data": _unref(companiesData)
      }, null, 8, ["form", "company", "production-units-data", "departments-data", "companies-data"])
    ]),
    address: _withCtx(() => [
      _createVNode(EmployeeAddressForm, { form: _ctx.form }, null, 8, ["form"])
    ]),
    bank: _withCtx(() => [
      _createVNode(EmployeeBankForm, { form: _ctx.form }, null, 8, ["form"])
    ]),
    pension: _withCtx(() => [
      _createVNode(EmployeePensionForm, { form: _ctx.form }, null, 8, ["form"])
    ]),
    credit_cards: _withCtx(() => [
      _createVNode(EmployeeCreditCardsForm, { form: _ctx.form }, null, 8, ["form"])
    ]),
    user: _withCtx(() => [
      _createVNode(EmployeeUserForm, {
        form: _ctx.form,
        employee: _ctx.employee
      }, null, 8, ["form", "employee"])
    ]),
    _: 1
  }, 8, ["tabs", "modelValue"]))
}
}

})